import * as THREE from 'three';
import { ballsFor, BALL_COLOR, ballRadius, type BallId, type VariantId } from '../config';
import type { RenderBall } from '../physics';

/**
 * Canvas texture: base colour plus a clean equator line (maps to a true great
 * circle, no distortion). The dark spots are NOT drawn here — a flat texture
 * wrapped on a sphere skews them near the poles; they're real disc geometry
 * instead (see `addSpots`) so they stay round and uniform everywhere.
 */
function makeBallTexture(hex: number): THREE.Texture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#' + hex.toString(16).padStart(6, '0');
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = 'rgba(0,0,0,0.22)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, size * 0.5);
  ctx.lineTo(size, size * 0.5);
  ctx.stroke();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** The six spot directions (face centres of a cube) — symmetric, visible from any angle. */
const SPOT_DIRS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

/** The variant's ball meshes; reads radius from config so straight rail gets a bigger ball. */
export class BallView {
  readonly group = new THREE.Group();
  private readonly meshes = {} as Record<BallId, THREE.Mesh>;
  private readonly ids: readonly BallId[];
  readonly radius: number;
  // Shared spot geometry/material so the dots are identical on every ball.
  private readonly spotGeo: THREE.CircleGeometry;
  private readonly spotMat: THREE.MeshStandardMaterial;

  /** `colorOverride` re-colours specific balls (e.g. a player's purchased custom
   *  cue-ball colour) — keyed by ball id, same hex format as BALL_COLOR. */
  constructor(variant: VariantId, colorOverride?: Partial<Record<BallId, number>>) {
    this.radius = ballRadius(variant);
    this.ids = ballsFor(variant);
    this.spotGeo = new THREE.CircleGeometry(this.radius * 0.12, 24);
    this.spotMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.4, metalness: 0 });
    for (const id of this.ids) {
      const color = colorOverride?.[id] ?? BALL_COLOR[id];
      const mat = new THREE.MeshPhysicalMaterial({
        color,
        map: makeBallTexture(color),
        roughness: 0.12,
        metalness: 0,
        clearcoat: 1,
        clearcoatRoughness: 0.08,
        // Low env reflection so the rim doesn't glow at grazing angles; the
        // overhead spotlights still give a tight, shiny highlight on the face.
        envMapIntensity: 0.3,
      });
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(this.radius, 48, 32), mat);
      mesh.castShadow = true;
      mesh.position.set(0, 0, this.radius);
      this.addSpots(mesh);
      this.meshes[id] = mesh;
      this.group.add(mesh);
    }
  }

  /** Attach the round disc spots to a ball, each lying flush on the surface. */
  private addSpots(mesh: THREE.Mesh): void {
    const zAxis = new THREE.Vector3(0, 0, 1);
    for (const [x, y, z] of SPOT_DIRS) {
      const dir = new THREE.Vector3(x, y, z);
      const dot = new THREE.Mesh(this.spotGeo, this.spotMat);
      dot.quaternion.setFromUnitVectors(zAxis, dir); // face the disc outward
      dot.position.copy(dir).multiplyScalar(this.radius * 1.001); // just on the surface
      mesh.add(dot); // child of the ball → spins with it
    }
  }

  apply(transforms: Record<BallId, RenderBall>): void {
    for (const id of this.ids) {
      const t = transforms[id];
      if (!t) continue;
      const m = this.meshes[id];
      m.position.set(t.pos.x, t.pos.y, t.pos.z);
      m.quaternion.set(t.orientation.x, t.orientation.y, t.orientation.z, t.orientation.w);
    }
  }

  position(id: BallId): THREE.Vector3 {
    return this.meshes[id].position.clone();
  }

  dispose(): void {
    this.spotGeo.dispose();
    this.spotMat.dispose();
    for (const id of this.ids) {
      const m = this.meshes[id];
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.group.clear();
  }
}
